import React, { useState } from "react";
import { Landmark, Loader2, RefreshCcw, AlertTriangle, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function LocationAnalysisCard({ dossier, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const la = dossier.location_analysis;
  const ex = dossier.extraction || {};
  const hasAddress = !!(ex.adresse || ex.ville);

  const analyze = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dossiers/${dossier.id}/analyze-location`);
      onUpdate(data.analysis);
      toast.success("Analyse cadastrale & ABF mise à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(false);
    }
  };

  // Compose ABF state
  let abfState = "unknown"; // unknown | no | yes
  if (la?.abf === null || la?.abf === undefined) abfState = "unknown";
  else if (Array.isArray(la.abf) && la.abf.length === 0) abfState = "no";
  else if (Array.isArray(la.abf) && la.abf.length > 0) abfState = "yes";

  const abfBg = abfState === "yes" ? "bg-status-erreurBg border-status-erreurBorder"
              : abfState === "no" ? "bg-status-completBg border-status-completBorder"
              : "bg-status-aVerifierBg border-status-aVerifierBorder";
  const abfText = abfState === "yes" ? "text-status-erreurText"
                : abfState === "no" ? "text-status-completText"
                : "text-status-aVerifierText";

  return (
    <div className="card-flat p-5" data-testid="location-analysis-card">
      <div className="flex items-center justify-between mb-3">
        <div className="overline flex items-center gap-1.5">
          <Landmark className="w-3 h-3" />
          Cadastre & ABF
        </div>
        {hasAddress && (
          <button
            onClick={analyze}
            disabled={busy}
            className="text-[10px] inline-flex items-center gap-1 text-norea-textMuted hover:text-norea-primary transition"
            data-testid="btn-analyze-location"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
            {la ? "Re-analyser" : "Analyser"}
          </button>
        )}
      </div>

      {!hasAddress && (
        <div className="text-xs text-norea-textMuted italic">
          Adresse manquante — pas d'analyse possible.
        </div>
      )}

      {hasAddress && !la && (
        <div className="text-xs text-norea-textMuted italic">
          Aucune analyse pour ce dossier.
          <button onClick={analyze} disabled={busy} className="ml-1 underline hover:text-norea-primary" data-testid="btn-analyze-location-inline">
            Lancer l'analyse →
          </button>
        </div>
      )}

      {la && (
        <div className="space-y-3">
          {/* Parcelle */}
          {la.parcelle ? (
            <div className="text-xs">
              <div className="text-[10px] uppercase tracking-wider text-norea-textMuted mb-1">Parcelle cadastrale</div>
              <div className="text-norea-text font-medium">
                Section {la.parcelle.section || "?"} · N° {la.parcelle.numero || "?"}
              </div>
              <div className="text-norea-textMuted mt-0.5">
                {la.parcelle.contenance_m2 && <>Superficie {la.parcelle.contenance_m2} m² · </>}
                {la.parcelle.commune}
                {la.parcelle.code_dep && ` (${la.parcelle.code_dep})`}
              </div>
            </div>
          ) : (
            <div className="text-xs text-norea-textMuted italic">Parcelle non identifiée.</div>
          )}

          {/* ABF */}
          <div className={`text-xs rounded-md border px-3 py-2 ${abfBg}`}>
            <div className={`flex items-center gap-1.5 font-medium ${abfText}`}>
              {abfState === "yes" && <AlertTriangle className="w-3.5 h-3.5" />}
              {abfState === "no" && <CheckCircle2 className="w-3.5 h-3.5" />}
              {abfState === "unknown" && <AlertCircle className="w-3.5 h-3.5" />}
              {abfState === "yes" && "Zone ABF — accord requis"}
              {abfState === "no" && "Hors zone ABF"}
              {abfState === "unknown" && "Analyse ABF indisponible"}
            </div>
            {abfState === "yes" && (
              <ul className="mt-1 space-y-0.5 list-disc list-inside text-norea-text">
                {la.abf.slice(0, 5).map((a, i) => (
                  <li key={i}>
                    <span className="font-medium">{a.category}</span> — {a.label}
                    {a.name && <span className="text-norea-textMuted"> · {a.name}</span>}
                  </li>
                ))}
              </ul>
            )}
            {abfState === "no" && (
              <div className="text-norea-textMuted mt-0.5 text-[11px]">
                Aucune servitude patrimoniale détectée à cette adresse.
              </div>
            )}
            {abfState === "unknown" && (
              <div className="text-norea-textMuted mt-0.5 text-[11px]">
                API IGN injoignable. Vérifier manuellement sur l'Atlas du Patrimoine.
              </div>
            )}
          </div>

          {la.analyzed_at && (
            <div className="text-[10px] text-norea-textMuted italic">
              Analysé {new Date(la.analyzed_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
